/**
 * Safe display formatters (APP-002 §13).
 *
 * Every formatter here is defensive: it never throws, never divides by zero,
 * and never produces misleading values such as `NaN%`, negative remaining
 * quota, or impossible percentages. Visual progress is clamped to 0–100 while
 * the textual value remains truthful (e.g. an over-limit account still shows
 * the real `used` count).
 *
 * These operate purely on backend-provided numbers. The browser never
 * recomputes quota, never infers "unlimited" from arbitrary numbers, and never
 * invents plan names.
 */

/** A nullable numeric quota/limit, where `null`/`undefined` means unlimited. */
export type Limit = number | null | undefined;

/**
 * Render an integer count with locale grouping. Non-finite or negative inputs
 * degrade to a neutral fallback rather than `NaN` / `-1`.
 */
export function formatCount(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  if (!Number.isFinite(value)) return fallback;
  return Math.trunc(value).toLocaleString();
}

/**
 * Render a limit, mapping `null`/`undefined` to "Unlimited". The backend uses
 * `null` as the explicit unlimited signal (OpenAPI DTO: `requests_limit?`).
 * We never infer unlimited from a sentinel like -1 in the browser; only `null`
 * is treated as unlimited because the contract says so.
 */
export function formatLimit(value: Limit): string {
  if (value === null || value === undefined) return 'Unlimited';
  if (!Number.isFinite(value)) return '—';
  return Math.trunc(value).toLocaleString();
}

/**
 * True only when the backend explicitly marks a limit as unlimited (`null`).
 * A numeric limit — even a very large one — is never treated as unlimited.
 */
export function isUnlimited(value: Limit): boolean {
  return value === null || value === undefined;
}

/**
 * Compute a usage percentage clamped to [0, 100] for visual progress, while
 * returning the truthful (unclamped) percentage for text. Never returns NaN.
 *
 * `Infinity` is avoided entirely: when the limit is unlimited the percentage
 * is defined as 0 (there is no meaningful "used percentage of infinity").
 */
export interface UsagePercent {
  /** Visual progress, clamped to 0–100. */
  clamped: number;
  /** Truthful percentage; may exceed 100 for over-limit accounts. */
  raw: number;
}

export function usagePercent(used: number, limit: Limit): UsagePercent {
  if (!Number.isFinite(used) || used < 0) return { clamped: 0, raw: 0 };
  if (isUnlimited(limit)) return { clamped: 0, raw: 0 };
  const safeLimit = limit as number;
  if (!Number.isFinite(safeLimit) || safeLimit <= 0) return { clamped: 0, raw: 0 };
  const raw = (used / safeLimit) * 100;
  return { clamped: Math.min(100, Math.max(0, raw)), raw };
}

/**
 * Format a quota-reset / window-end date for display. Accepts RFC 3339
 * timestamps. Degrades gracefully on malformed input.
 */
export function formatDate(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Format a timestamp with time for contexts that need more precision.
 */
export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Render a remaining count, mapping unlimited to "Unlimited". Never returns a
 * negative number from a backend authority — if the backend reports a
 * negative, we surface it truthfully but the visual progress is clamped.
 */
export function formatRemaining(remaining: number | null | undefined): string {
  if (remaining === null || remaining === undefined) return 'Unlimited';
  return formatCount(remaining);
}

/**
 * Humanize a billing interval token (`monthly` / `annual`) for display. Falls
 * back to title-casing unknown values rather than crashing.
 */
export function formatBillingInterval(value: string | null | undefined): string {
  if (!value) return '—';
  if (value === 'monthly') return 'Monthly';
  if (value === 'annual') return 'Annual';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/* --------------------------------------------------------------------------
 * Usage-specific formatters (APP-003 §23).
 *
 * These extend the APP-002 helpers with usage-page presentation concerns
 * (reset dates, quota windows, relative reset hints, rate-limit rendering).
 * They are presentation-only derivations built on backend-provided numbers and
 * timestamps; the browser never recomputes quota authority (APP-003 §4).
 * ------------------------------------------------------------------------ */

// Reuse a single Intl.NumberFormat per locale for the integer count path. The
// formatCount helper above already calls toLocaleString(); this object is only
// for the dedicated usage integer formatters below, which want explicit
// groupings consistently.
const integerFormatter =
  typeof Intl !== 'undefined' ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }) : null;

/**
 * Render a usage count (used / remaining) as a locale-grouped integer. Differs
 * from `formatCount` only in intent: callers signal this is a usage figure so a
 * future formatting change (e.g. compact "1.2K") can be applied uniformly.
 * Degrades to a neutral fallback for non-finite values; never returns NaN.
 */
export function formatUsageValue(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  if (!Number.isFinite(value)) return fallback;
  if (integerFormatter) return integerFormatter.format(Math.trunc(value));
  return Math.trunc(value).toLocaleString();
}

/**
 * Render a usage limit, mapping `null`/`undefined` to "Unlimited". Mirrors
 * `formatLimit` semantics but named for the usage-page context.
 */
export function formatUsageLimit(value: Limit): string {
  return formatLimit(value);
}

/**
 * Render a usage percentage with one decimal place for the fractional part when
 * it is meaningful, clamped to 0–100 for display. Returns "Unlimited" for an
 * unlimited limit (there is no meaningful percentage of infinity) and "—" for a
 * zero/negative limit. Never returns NaN.
 *
 * The textual value can legitimately exceed 100% for an over-limit account; use
 * `usagePercent(...).raw` when the truthful unclamped value is required.
 */
export function formatUsagePercent(used: number, limit: Limit): string {
  if (!Number.isFinite(used) || used < 0) return '0%';
  if (isUnlimited(limit)) return 'Unlimited';
  const safeLimit = limit as number;
  if (!Number.isFinite(safeLimit) || safeLimit <= 0) return '—';
  const pct = (used / safeLimit) * 100;
  return `${pct.toFixed(1)}%`;
}

/**
 * Parse a reset / window timestamp into a Date, returning null on malformed
 * input. Shared by the reset formatters below so each one degrades gracefully.
 */
function parseResetDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/**
 * Format a quota-reset / window-end date as an absolute local date. Uses
 * Intl.DateTimeFormat with month name + day + year so the value is unambiguous
 * across locales (never a numeric date like 08/09/26). APP-003 §24.
 *
 * Example: "Resets August 31, 2026" → the date portion "August 31, 2026".
 */
export function formatResetDate(value: string | null | undefined, fallback = '—'): string {
  const date = parseResetDate(value);
  if (!date) return fallback;
  if (typeof Intl !== 'undefined') {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }
  return date.toLocaleDateString();
}

/**
 * Format a reset timestamp as an absolute local date + time with timezone
 * abbreviation (e.g. "August 31, 2026 at 7:00 PM CDT"). APP-003 §24. The
 * authoritative backend timestamp is rendered verbatim (offsets preserved);
 * the browser never modifies it. Degrades gracefully on malformed input.
 */
export function formatResetDateTime(value: string | null | undefined, fallback = '—'): string {
  const date = parseResetDate(value);
  if (!date) return fallback;
  if (typeof Intl !== 'undefined') {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  }
  return date.toLocaleString();
}

/**
 * Compute a relative reset helper ("12 days remaining") from a reset timestamp.
 * Returns null when the timestamp is malformed or already in the past so the
 * caller can omit the helper rather than show "0 days remaining" misleadingly.
 * APP-003 §9: absolute dates are the primary signal; this is a helper only.
 *
 * Uses rounded whole days. Caps the display at whole units (no "12.4 days").
 */
export function formatRelativeReset(value: string | null | undefined): string | null {
  const date = parseResetDate(value);
  if (!date) return null;
  const now = Date.now();
  const diffMs = date.getTime() - now;
  if (diffMs <= 0) return null;
  const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return null;
  if (days === 1) return '1 day remaining';
  if (days < 60) return `${days} days remaining`;
  const months = Math.round(days / 30);
  if (months <= 1) return 'about 1 month remaining';
  return `about ${months} months remaining`;
}

/**
 * Format a plan rate limit (requests/minute). The backend returns -1 for
 * unlimited (Enterprise); translate that to "Unlimited" for display. A zero or
 * negative-but-not-unlimited value degrades to a neutral fallback rather than
 * showing "0/min". APP-003 §12.
 *
 * Returns an object so the caller can render the label and the "unlimited"
 * flag separately (e.g. to omit a finite-only progress bar).
 */
export interface RateLimitPresentation {
  label: string;
  unlimited: boolean;
}

export function formatRateLimit(perMinute: number | null | undefined): RateLimitPresentation {
  if (perMinute === null || perMinute === undefined) return { label: '—', unlimited: false };
  // -1 is the backend's unlimited sentinel (PLAN_QUOTA_REFERENCE / planawareness).
  if (perMinute === -1) return { label: 'Unlimited', unlimited: true };
  if (!Number.isFinite(perMinute) || perMinute < 0) return { label: '—', unlimited: false };
  return { label: `${formatUsageValue(perMinute)} requests/minute`, unlimited: false };
}

/* --------------------------------------------------------------------------
 * Currency / price formatting (APP-006 §42).
 *
 * Prices are sourced from the authoritative locked catalog
 * (internal/billing/catalog.go lockedAmounts, integer cents, USD only). The
 * browser never recomputes a price from a Stripe object; these helpers only
 * render backend-/catalog-authoritative amounts. Enterprise/Custom is rendered
 * as "Custom", never "$0". Tax-inclusive language is intentionally absent (the
 * contract provides no tax data).
 * ------------------------------------------------------------------------ */

/**
 * Plan currency. Launch currency is fixed to USD by the backend catalog
 * (internal/billing/catalog.go CurrencyUSD). We type it narrowly so a future
 * currency surfaces at the call site rather than silently changing formatting.
 */
export type BillingCurrency = 'USD';

// A single shared currency formatter per locale. USD is the only launch
// currency; the catalog uses integer cents, so we keep 2 fractional digits to
// preserve meaningful cents (never round away $0.01 differences).
const currencyFormatter =
  typeof Intl !== 'undefined'
    ? new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : null;

/**
 * Format an integer-cent amount as a currency string. Degrades to a neutral
 * fallback for non-finite or negative inputs. `null`/`undefined` maps to
 * "Custom" (Enterprise/contract pricing) — never "$0".
 *
 * `currency` is accepted so a future launch currency can be surfaced at the
 * call site; today the backend catalog is fixed to USD, so a non-USD value
 * builds a dedicated Intl formatter on demand.
 */
export function formatPrice(cents: number | null | undefined, currency: BillingCurrency = 'USD'): string {
  if (cents === null || cents === undefined) return 'Custom';
  if (!Number.isFinite(cents) || cents < 0) return 'Custom';
  const formatter =
    currency === 'USD' && currencyFormatter
      ? currencyFormatter
      : new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  return formatter.format(cents / 100);
}

/**
 * Format a price with its billing cycle, e.g. "$24.99/month" or "$269.00/year".
 * `null`/`undefined` amounts (custom/Enterprise pricing) render as "Custom" with
 * no cycle suffix. The interval is authoritative backend vocabulary.
 */
export function formatPriceWithCycle(
  cents: number | null | undefined,
  interval: 'monthly' | 'annual' | string | null | undefined,
  currency: BillingCurrency = 'USD',
): string {
  if (cents === null || cents === undefined) return 'Custom';
  const price = formatPrice(cents, currency);
  if (price === 'Custom') return 'Custom';
  if (interval === 'annual') return `${price}/year`;
  if (interval === 'monthly') return `${price}/month`;
  return price;
}
